import { PrototypeApiError } from './apiClient';
import { clearPasswordResetToken, readPasswordResetToken } from './authTokenUrl';
import { byId } from './domUtils';
import { recordRuntimeError } from './prototypeRuntimeSupport';
import type { PrototypeState } from './controllerState';
import type { Root } from './prototypeTypes';

const validationGenerations = new WeakMap<Root, number>();

export async function validateCapturedPasswordResetToken(root: Root, state: PrototypeState): Promise<void> {
	const generation = (validationGenerations.get(root) ?? 0) + 1;
	validationGenerations.set(root, generation);
	const token = readPasswordResetToken();
	if (!token) {
		showTerminalValidationState(root);
		return;
	}

	showCheckingState(root);
	try {
		await state.runtime.api.validateResetPasswordToken({ token });
		if (!isCurrentValidation(root, generation, token)) return;
		showValidForm(root);
	} catch (error) {
		if (!isCurrentValidation(root, generation, token)) return;
		recordRuntimeError(state.runtime, error);
		if (isTerminalTokenFailure(error)) {
			clearPasswordResetToken();
			showTerminalValidationState(root);
			return;
		}
		showRetryableValidationState(root, validationFailureMessage(error));
	}
}

function isCurrentValidation(root: Root, generation: number, token: string): boolean {
	return validationGenerations.get(root) === generation && readPasswordResetToken() === token;
}

function isTerminalTokenFailure(error: unknown): boolean {
	return error instanceof PrototypeApiError
		&& error.status === 400
		&& (error.body as { error?: string } | null)?.error === 'invalid_or_expired_reset_token';
}

function validationFailureMessage(error: unknown): string {
	if (error instanceof PrototypeApiError && error.status === 0) {
		return 'We could not check this link because of a connection problem. Check your connection, then try again.';
	}
	if (error instanceof PrototypeApiError && error.status === 429) {
		return 'Too many checks were requested. Please wait a minute, then try again.';
	}
	return 'Password reset validation is temporarily unavailable. Please try again.';
}

function showCheckingState(root: Root): void {
	setDisplay(root, 'resetPasswordForm', 'none');
	setDisplay(root, 'resetPasswordError', 'none');
	setDisplay(root, 'resetPasswordValidationError', 'none');
	setDisplay(root, 'resetPasswordChecking', 'block');
}

function showValidForm(root: Root): void {
	setDisplay(root, 'resetPasswordChecking', 'none');
	setDisplay(root, 'resetPasswordError', 'none');
	setDisplay(root, 'resetPasswordValidationError', 'none');
	setDisplay(root, 'resetPasswordForm', '');
}

function showTerminalValidationState(root: Root): void {
	setDisplay(root, 'resetPasswordChecking', 'none');
	setDisplay(root, 'resetPasswordForm', 'none');
	setDisplay(root, 'resetPasswordValidationError', 'none');
	setDisplay(root, 'resetPasswordError', 'block');
}

function showRetryableValidationState(root: Root, message: string): void {
	setDisplay(root, 'resetPasswordChecking', 'none');
	setDisplay(root, 'resetPasswordForm', 'none');
	setDisplay(root, 'resetPasswordError', 'none');
	setDisplay(root, 'resetPasswordValidationError', 'block');
	const detail = byId(root, 'resetPasswordValidationErrorDetail');
	if (detail) detail.textContent = message;
}

function setDisplay(root: Root, id: string, display: string): void {
	const element = byId(root, id);
	if (element) element.style.display = display;
}
